import { Autocomplete, Divider, Paper, TextField, Drawer, Box } from "@mui/material"
import { useCallback, useMemo, useState } from "react";
import { Controller, useForm, UseFormReturn } from "react-hook-form";
import { useDependencyInjection } from "../../config/di/dependency_injection_context";
import { usePageLoader } from "../contexts/page_content_context";
import { usePageStartBootstrap } from "../hooks/use_page_start_bootstrap";
import { zodResolver } from "@hookform/resolvers/zod";
import { defaultSubcontaSimplificadoForm, SubcontaSimplificadoForm, SubcontaSimplificadoSchema } from "../presentation/schemas/SubContaSimplificado";
import { SubcontaDropdown } from "../../features/rh/domain/models/subconta_dropdown";
import { DefaultFormAutocomplete } from "./default_form_autocomplete";
import { FormRadioGroupInput } from "./masked_inputs/react_forms/form_radio_group_input";

import DynamicIcon from "./dynamic_icon";
import DefaultFormInput from "./default_form_input";
import PrimaryButton from "./primary_button";

/**
 * DropDownSubconta
 */

type Props<F extends {}> = {
  name: keyof F,
  label?: string,
  newButtonLabel?: string,
  required?: boolean,
  readOnly?: boolean,
  placeholder?: string,
  reactHookForm: UseFormReturn<F>,

};

export const DropDownSubconta = <F extends {},>({
  name,
  newButtonLabel,
  label = 'Subconta',
  required,
  readOnly,
  placeholder,
  reactHookForm,

}: Props<F>) => {

  const [openModal, setOpenModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const { pushErrorNotification, pushInfoNotification } = usePageLoader();

  const [subcontaPaiDropDown, setSubcontaPaiDropDown] = useState<SubcontaDropdown[]>();
  const [subcontaDropDown, setSubcontaDropDown] = useState<SubcontaDropdown[]>([]);

  const {
    commons: {
      getSubcontasDropDownUseCase,
      createSubcontaSimplificadoUseCase,
    }

  } = useDependencyInjection();


  const hookForm = useForm<SubcontaSimplificadoForm>({
    reValidateMode: 'onSubmit',
    mode: 'onSubmit',
    defaultValues: defaultSubcontaSimplificadoForm,
    resolver: zodResolver(SubcontaSimplificadoSchema),
  });

  //Load Subcontas DropDown

  const loadSubcontasDropDown = useCallback(async () => {
    try {

      const result = await getSubcontasDropDownUseCase.getSubcontasDropDown();
      setSubcontaDropDown(result);
      setSubcontaPaiDropDown(result);
    } catch (e: unknown) {
      throw new Error('Falha ao buscar Subcontas!');
    }
  }, [
    getSubcontasDropDownUseCase,
  ]);

  //Create Subconta

  const hanldleSalvarSubconta = useCallback(async (form: SubcontaSimplificadoForm) => {
    try {
      setSaving(true);

      const idSubconta = await createSubcontaSimplificadoUseCase.createSubcontaSimplificado(
        {
          ativo: form.ativo,
          codigo: form.codigo,
          descricao: form.descricao,
          idSubcontaPai: form.idSubcontaPai
        }
      );

      reactHookForm.setValue(name as any, idSubconta as any);
      pushInfoNotification("SUCESSO", "Subconta criado com sucesso!!", { isAutoFinish: true })

    } catch (e: unknown) {
      pushErrorNotification("Erro", ["Falha ao salvar Subconta"], { isAutoFinish: true })
    } finally {
      setSaving(false);
      loadSubcontasDropDown();
      setOpenModal(false);
    }

  }, [createSubcontaSimplificadoUseCase, loadSubcontasDropDown, name, pushErrorNotification, pushInfoNotification, reactHookForm]);

  usePageStartBootstrap(async () => {
    loadSubcontasDropDown()
  })

  // Add Subconta Button
  const CustomPaper = useMemo(
    () =>
      ({ children, ...props }: any) => (
        <Paper {...props}>
          <div>
            {children}
            <Divider />
            <div>
              <div
                className="py-2 px-4 cursor-pointer rounded-[4px] font-semibold flex items-center gap-2 text-sm text-primary-green hover:bg-hover-light-gray"
                onMouseDown={() => setOpenModal(true)}
                role="button"
                tabIndex={0}
              >
                <DynamicIcon
                  nameIcon="Add"
                  size={20}
                  className="text-primary-green"
                />
                <span>{newButtonLabel ?? "Novo"}</span>
              </div>
            </div>
          </div>
        </Paper>
      ),
    [newButtonLabel]
  );

  return (
    <>

      {/*Centro Custo DropDown */}

      <Controller
        name={name as any}
        control={reactHookForm.control}
        render={({ field, formState }) => {
          let helperText: string | null = null;

          if (formState.errors[name as keyof typeof formState.errors]) {
            const fieldError = formState.errors[name] as any;
            if (fieldError?.message && typeof fieldError.message === 'string') {
              helperText = fieldError.message;
            }
          }


          return (
            <Autocomplete
              fullWidth={true}
              size="small"
              disablePortal
              options={subcontaDropDown}
              getOptionKey={opt => opt.id}
              getOptionLabel={(opt) => opt.title}
              isOptionEqualToValue={(opt, val) => opt.id === val.id}
              slots={{
                paper: CustomPaper,
              }}
              sx={{ minHeight: 45 }}
              readOnly={readOnly}
              disabled={formState.disabled}
              value={subcontaDropDown.find(x => x.id === field.value) ?? null}
              onChange={(_, newValue) => field.onChange(newValue ? newValue.id : undefined)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  label={label}
                  error={helperText !== null}
                  helperText={helperText}
                  placeholder={placeholder}
                  required={required}
                />
              )}
            />
          );
        }}
      />

      {/* Add subconta  */}

      <Drawer
        anchor="right"
        open={openModal}
        component={"form"}
        onSubmit={hookForm.handleSubmit(hanldleSalvarSubconta)}
        onClose={() => setOpenModal(false)}
        style={{ zIndex: 1300 }}
        PaperProps={{ style: { width: 500, padding: 20, display: "flex", flexDirection: "column", height: "100%" } }}
      >
        <Box sx={{ flexGrow: 1, overflowY: "auto" }}>
          <h2 className="text-lg font-semibold mb-4">Adicionar Subconta</h2>


          <div className="flex flex-col items-start w-[25rem] gap-4">
            <FormRadioGroupInput
              name='ativo'
              label='Ativo'
              options={[
                { id: 1, label: 'Ativo' },
                { id: 0, label: 'Bloqueado' },
              ]}
              reactHookForm={hookForm} />

            <DefaultFormInput
              name='codigo'
              label='Código'
              required={true}
              reactHookForm={hookForm} />

            <div className="w-full ">
              <DefaultFormAutocomplete
                name='idSubcontaPai'
                required={false}
                label='Subconta Principal'
                options={subcontaPaiDropDown ?? []}
                getOptionLabel={x => x.title}
                getOptionKey={x => x.id}
                reactHookForm={hookForm} />
            </div>


            <DefaultFormInput
              name='descricao'
              label='Descrição'
              multiline={true}
              minRows={6}
              required={false}
              reactHookForm={hookForm} />


          </div>
        </Box>

        <Box
          sx={{
            position: "sticky",
            bottom: 0,
            width: "100%",
            background: "white",
            padding: 2,
            borderTop: "1px solid #e0e0e0",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >

          <Box
            className="p-2 hover:bg-hover-light-gray cursor-pointer dark:hover:bg-border-dark-gray rounded-[4px] font-semibold"
            onClick={() => setOpenModal(false)}
          >
            <h1 className="text-sm text-black dark:text-white">Cancelar</h1>
          </Box>

          <Box width="120px">
            {saving ? (
              <PrimaryButton buttonColor="primary-green">
                Salvando...
              </PrimaryButton>
            ) : (
              <PrimaryButton
                buttonColor="primary-green"
                type="submit"

              >
                Salvar
              </PrimaryButton>
            )}
          </Box>
        </Box>
      </Drawer>

    </>
  );
};
